import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import alert from '../utils/alertService';
import { createModalHandlers, createButtonHandler, preventBodyScroll } from '../utils/modalUtils';
import './Giveaways.css';

const Giveaways = () => {
  usePageTitle('Giveaways');
  const { user } = useAuth();
  const [giveaways, setGiveaways] = useState([]);
  const [userPoints, setUserPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedGiveaway, setSelectedGiveaway] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(false);

  useEffect(() => {
    fetchGiveaways();
    if (user) {
      fetchUserPoints();
    }
  }, [user]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showClaimModal) {
      preventBodyScroll(true);
    } else {
      preventBodyScroll(false);
    }
    return () => {
      preventBodyScroll(false);
    };
  }, [showClaimModal]);

  const fetchGiveaways = async () => {
    try {
      const response = await api.get('/giveaways');
      // Handle different response structures
      const giveawaysData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.data && Array.isArray(response.data.data) 
          ? response.data.data 
          : []);
      
      console.log('Fetched giveaways:', giveawaysData);
      setGiveaways(giveawaysData);
    } catch (error) {
      console.error('Error fetching giveaways:', error);
      console.error('Error response:', error.response);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPoints = async () => {
    if (!user) {
      console.log('No user logged in, skipping points fetch');
      setUserPoints(0);
      return;
    }
    
    try {
      console.log('Fetching points for user:', user.id, user.email);
      const response = await api.get('/my-points');
      console.log('Points response:', response.data);
      console.log('Response data type:', typeof response.data);
      console.log('Response data keys:', Object.keys(response.data || {}));
      
      // API returns { points: number, points_history: array }
      let points = 0;
      if (response.data) {
        if (typeof response.data === 'number') {
          points = response.data;
        } else if (response.data.points !== undefined) {
          points = Number(response.data.points) || 0;
        } else if (response.data.data && response.data.data.points !== undefined) {
          points = Number(response.data.data.points) || 0;
        }
      }
      
      console.log('Extracted points:', points);
      setUserPoints(points);
    } catch (error) {
      console.error('Error fetching points:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setUserPoints(0);
    }
  };

  const handleClaim = (giveaway) => {
    setSelectedGiveaway(giveaway);
    setShowClaimModal(true);
  };

  const confirmClaim = async () => {
    try {
      const response = await api.post(`/giveaways/${selectedGiveaway.id}/claim`);
      alert.success('Giveaway claimed successfully!');
      setShowClaimModal(false);
      fetchGiveaways();
      fetchUserPoints();
    } catch (error) {
      console.error('Error claiming giveaway:', error);
      alert.error(error.response?.data?.message || 'Error claiming giveaway');
    }
  };

  if (loading) {
    return (
      <div className="giveaways-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading giveaways...</p>
      </div>
    );
  }

  return (
    <div className="giveaways-container">
      <div className="giveaways-header">
        <h1>🎁 GIVEAWAYS</h1>
        <p className="giveaways-subtitle">Redeem your points for amazing rewards</p>
        {user ? (
          <div className="user-points-display">
            <span className="points-label">Your Points:</span>
            <span className="points-value">{userPoints}</span>
          </div>
        ) : (
          <div className="user-points-display">
            <span className="points-label">Please login to view your points</span>
          </div>
        )}
      </div>

      {giveaways.length === 0 ? (
        <div className="no-giveaways">
          <p>No giveaways available at the moment.</p>
        </div>
      ) : (
        <div className="giveaways-grid">
          {giveaways.map((giveaway) => {
            const canClaim = userPoints >= (giveaway.required_points || 0);
            const claimedCount = giveaway.claimed_count || 0;
            const quantity = giveaway.quantity || 0;
            const isAvailable = quantity === 0 || claimedCount < quantity;

            return (
              <div key={giveaway.id} className="giveaway-card">
                <div className="giveaway-image-wrapper">
                  {giveaway.image_url || giveaway.image ? (
                    <img
                      src={giveaway.image_url ? `${giveaway.image_url}?t=${Date.now()}` : `https://aliensteam.com/storage/${giveaway.image}?t=${Date.now()}`}
                      alt={giveaway.title}
                      className="giveaway-image"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/300x300/0a0e27/00ff88?text=Giveaway';
                      }}
                    />
                  ) : (
                    <div className="giveaway-placeholder">
                      <span className="placeholder-icon">🎁</span>
                    </div>
                  )}
                  {!isAvailable && (
                    <div className="giveaway-unavailable">Out of Stock</div>
                  )}
                </div>
                <div className="giveaway-info">
                  <h3 className="giveaway-title">{giveaway.title}</h3>
                  {giveaway.description && (
                    <p className="giveaway-description">{giveaway.description}</p>
                  )}
                  <div className="giveaway-footer">
                    <div className="points-required">
                      <span className="points-icon">⭐</span>
                      <span>{giveaway.required_points || 0} Points</span>
                    </div>
                    {quantity > 0 && (
                      <div className="giveaway-stock">
                        {quantity - claimedCount} left
                      </div>
                    )}
                  </div>
                  <button
                    className={`btn-claim ${canClaim && isAvailable ? 'available' : 'unavailable'}`}
                    onClick={() => handleClaim(giveaway)}
                    disabled={!canClaim || !isAvailable}
                  >
                    {canClaim && isAvailable
                      ? '🎁 Claim Now'
                      : userPoints < (giveaway.required_points || 0)
                      ? `Need ${(giveaway.required_points || 0) - userPoints} more points`
                      : 'Not Available'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showClaimModal && selectedGiveaway && (
        <div 
          className="modal-overlay" 
          {...createModalHandlers(() => setShowClaimModal(false))}
        >
          <div 
            className="modal-content giveaway-modal" 
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            <button 
              className="modal-close" 
              {...createButtonHandler(() => setShowClaimModal(false))}
            >
              ×
            </button>
            <h2>Claim Giveaway</h2>
            <div className="claim-giveaway-info">
              <img
                src={selectedGiveaway.image_url ? `${selectedGiveaway.image_url}?t=${Date.now()}` : (selectedGiveaway.image ? `https://aliensteam.com/storage/${selectedGiveaway.image}?t=${Date.now()}` : 'https://via.placeholder.com/150x150/0a0e27/00ff88?text=Giveaway')}
                alt={selectedGiveaway.title}
                className="claim-giveaway-image"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/150x150/0a0e27/00ff88?text=Giveaway';
                }}
              />
              <div>
                <h3>{selectedGiveaway.title}</h3>
                <p className="claim-points-required">
                  <span className="points-icon">⭐</span>
                  {selectedGiveaway.required_points || 0} Points Required
                </p>
                <p className="claim-current-points">
                  Your Points: <strong>{userPoints}</strong>
                </p>
                {selectedGiveaway.description && (
                  <p className="claim-description">{selectedGiveaway.description}</p>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowClaimModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={confirmClaim}>
                Confirm Claim
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Giveaways;

